import Phaser from 'phaser';
import type { BiomeId } from '../../content/biomes';

/**
 * Time of day per world (docs/ART_DIRECTION.md): a light over the whole lawn view. A multiply tint
 * sets the color of the light; an optional faint additive glow lifts it. Both are uniform, so
 * every tile keeps its contrast with grass: tools/cvd_check.py re-checks the tile sheet under each
 * mood (keep its MOODS table in sync with this one).
 */
export interface Mood {
  name: string;
  /** Multiplied over everything (0xffffff = no change). */
  light: number;
  /** Added on top, faintly: the warmth or freshness in the air. */
  glow?: { color: number; alpha: number };
}

export const MOODS: Readonly<Record<BiomeId, Mood>> = {
  backyard: { name: 'morning', light: 0xf1f6ff, glow: { color: 0xfff3d6, alpha: 0.05 } },
  park: { name: 'midday', light: 0xffffff },
  farm: { name: 'golden hour', light: 0xffe6c2, glow: { color: 0x5a3000, alpha: 0.12 } },
  golf: { name: 'late afternoon', light: 0xfff1dc },
  cemetery: { name: 'dusk', light: 0xd9d2ee, glow: { color: 0x1a1040, alpha: 0.08 } },
  estate: { name: 'overcast', light: 0xeef0f2 },
  stadium: { name: 'floodlit evening', light: 0xe6ecff, glow: { color: 0xffffff, alpha: 0.04 } },
  meadow: { name: 'summer afternoon', light: 0xfff8e8, glow: { color: 0xfff0c0, alpha: 0.05 } },
};

/**
 * Lays the world's light over the scene's camera view (above the lawn, mower and effects; the HUD
 * is its own scene and stays untouched). Fixed to the camera, so it covers whatever is in view.
 */
export function applyMood(scene: Phaser.Scene, biome: BiomeId, depth: number): void {
  const mood = MOODS[biome];
  const { width, height } = scene.scale.gameSize;
  // Generous size: the overlay is fixed to the camera and clipped by its viewport.
  const w = width * 2;
  const h = height * 2;
  if (mood.light !== 0xffffff) {
    scene.add
      .rectangle(0, 0, w, h, mood.light)
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(depth)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
  }
  if (mood.glow) {
    scene.add
      .rectangle(0, 0, w, h, mood.glow.color, mood.glow.alpha)
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(depth + 1)
      .setBlendMode(Phaser.BlendModes.ADD);
  }
}
